import React, { useState } from "react";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { useDispatch, useSelector } from "react-redux";
import { DialogActions } from "@mui/material";
import Teachr from "src/Objects/models/Teachr";
import DialogLayout from "src/Components/DialogLayout";
import { DataDilogLayout } from "src/Objects/interfaces/AppInterface";
import { useDeclarationContexte } from "src/Contexts/DeclarationContexte";
import { SET_COURSE_DECLARATION_INFO } from "src/Redux/Reducers/DeclarationCourseReducers";
import { capitalizeFirstLetter, displayError } from "src/Utils/Function";
import { BrandContent, useTailwindBrand } from "src/Components/shared";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "src/config/environment.config";
import ButtonField from "src/Components/Ui/ButtonField";
import { normalizeSessionDescriptionOverride, sessionDescriptionOverrideError } from "src/Utils/sessionDescriptionOverride";
import IndexStep from "../IndexStep";
import ClientRadioList from "../Components/ClientRadioList";
import { useTravelScenario } from "../Travel/TravelModeSelector";
import { useSessionDescriptionCheck } from "../useSessionDescriptionCheck";
import SessionDescriptionCard from "../Components/SessionDescriptionCard";

const FirstStep = () => {
  const dispatch = useDispatch();
  const { classes, common, brandConfig } = useTailwindBrand();
  const {
    handleChangeStep,
    generalStep,
    clients,
    totalItems,
    currentPage,
    nameSearch,
    setNameSearch,
    paginate,
    pageLimit,
  } = useDeclarationContexte();

  const fullName = generalStep?.firstStep?.child ? `${capitalizeFirstLetter(generalStep.firstStep.child.childFirstName!)} ${generalStep.firstStep.child.childLastName?.toUpperCase()!}` : `Ajouter un ${getActiveClientBrandConfig()}`;

  const [value, setValue] = React.useState("");
  const [errorMsg, setErrorMsg] = React.useState("");
  const [error, setError] = useState({ type: false });
  const [child, setChild] = React.useState<any>();
  const [dateTimeTdpMoment, setDateTimeTdpMoment] = useState<any>();
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<"ponctuel" | "multiple" | null>(null);
  const teachr: Teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
  const { checkTravelScenario, travelModals, travelLoading } = useTravelScenario(teachr);
  const { checkSessionDescription, descriptionModal, descriptionLoading } = useSessionDescriptionCheck(teachr);
  // Déroulement propre à ce cours (surcharge de la description habituelle), conservé entre les étapes
  const [sessionDescriptionOverride, setSessionDescriptionOverride] = useState<string | null>(
    generalStep?.firstStep?.sessionDescriptionOverride ?? null
  );
  const [genericDescription, setGenericDescription] = useState<string | null>(null);
  const [overrideError, setOverrideError] = useState("");
  const normalizedOverride = () => normalizeSessionDescriptionOverride(sessionDescriptionOverride, genericDescription);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNameSearch("");
  };

  const validate = () => {
    if (!generalStep?.firstStep) {
      setError({ type: true });
      setErrorMsg(`Veuillez sélectionner un ${getActiveClientBrandConfig()}, avant de choisir le type de ${getActivePrestationBrandConfig()}`);
      return false;
    }
    setError({ type: false });
    setErrorMsg("");
    const descriptionError = sessionDescriptionOverrideError(sessionDescriptionOverride);
    setOverrideError(descriptionError);
    return descriptionError === "";
  };
  const handleSubmit = () => {
    if (value && child) {
      handleClose();
      if (generalStep?.secondStep) {
        dispatch({
          type: SET_COURSE_DECLARATION_INFO,
          payload: {
            step: value,
            firstStep: {
              childId: parseInt(value),
              child,
              dateTimeTdpMoment: dateTimeTdpMoment.toDate().toISOString(),
              sessionDescriptionOverride: normalizedOverride(),
            },
            secondStep: generalStep.secondStep,
            thirdStep: generalStep.thirdStep ?? null,
            cours: generalStep.cours ?? null,
          },
        });
      } else {
        dispatch({
          type: SET_COURSE_DECLARATION_INFO,
          payload: {
            firstStep: {
              childId: parseInt(value),
              child,
              dateTimeTdpMoment: dateTimeTdpMoment.toDate().toISOString(),
              sessionDescriptionOverride: normalizedOverride(),
            },
          },
        });
      }
    }
  };
  const proceedToNextStep = (nextStep: number, isMonth: boolean) => {
    // la surcharge saisie sur cette carte part avec l'étape 1
    const firstStep = { ...generalStep.firstStep, sessionDescriptionOverride: normalizedOverride() };
    if (generalStep?.secondStep) {
      dispatch({
        type: SET_COURSE_DECLARATION_INFO,
        payload: {
          step: nextStep,
          firstStep,
          secondStep: generalStep.secondStep ?? null,
          thirdStep: generalStep.thirdStep ?? null,
          cours: isMonth,
        },
      });
      handleChangeStep(nextStep);
    } else if (generalStep?.firstStep) {
      dispatch({
        type: SET_COURSE_DECLARATION_INFO,
        payload: {
          step: nextStep,
          firstStep,
          cours: isMonth,
        },
      });
      handleChangeStep(nextStep);
    }
  };
  const handleNextStep = (nextStep: number, isMonth: boolean) => {
    if (validate()) {
      // P4 : description générique obligatoire, puis scénarios distance (P5)
      checkSessionDescription(() =>
        checkTravelScenario(generalStep?.firstStep?.child, () => proceedToNextStep(nextStep, isMonth))
      );
    }
  };

  const activeClientList: DataDilogLayout = {
    title: `Mes ${getActiveClientBrandConfig("s")}`,
    text: (
      <ClientRadioList
        teachr={teachr}
        childs={clients}
        value={value}
        nameSearch={nameSearch}
        onSearchChange={setNameSearch}
        totalItems={totalItems}
        currentPage={currentPage}
        onPageChange={paginate}
        pageLimit={pageLimit}
        setValue={setValue}
        handleClose={handleClose}
        handleClickOpen={handleOpen}
        setChild={setChild}
        setDateTimeTdpMoment={setDateTimeTdpMoment}
        setLoad={setLoad}
        load={load}
      />
    ),
    valid: "Valider",
    cancel: "Annuler",
    open,
    handleClose,
  };

  return (
    <>
      {travelModals}
      {descriptionModal}
      <DialogLayout
        title={activeClientList.title}
        text={activeClientList.text}
        valid={activeClientList.valid}
        cancel={activeClientList.cancel}
        open={activeClientList.open}
        handleCloseIcon={handleClose}
        customDialogActions="hidden"
        maxWidth="md"
        btnChildren={
          <DialogActions sx={{ py: 2.5, px: 3, borderTop: '1px solid', borderColor: 'divider' }} className={common.body}>
            <ButtonField
              variant="contained"
              disabled={load}
              loading={load}
              onClick={handleSubmit}
              label="Valider"
              className={`${!value && "invisible"} w-full`}
            />
          </DialogActions>
        }
      />

      <IndexStep
        title={`Déclaration de ${getActivePrestationBrandConfig()} 📚`}
        body={
          <>
            <BrandContent
              tag="p"
              children={`Dans cette section, vous pouvez déclarer ${getActivePrestationBrandConfig("s dispensées", "tous les ", "toutes les ", " dispensés")} aux ${getActiveClientBrandConfig("s")} pour le mois actuel, ou bien déclarer ${getActivePrestationBrandConfig("", "un ", "une ")} spécifique qui a eu lieu de manière ponctuelle.`}
              type="description"
              className="w-full mb-4"
            />

            <p className={`${common.body} ${classes.primaryText} font-semibold mb-3`}>
              Pour quel {getActiveClientBrandConfig("")} souhaitez-vous déclarer {getActivePrestationBrandConfig("", "un ", "une ")} ?
            </p>
            <div className="mb-6">
              <ButtonField
                variant="outlined"
                onClick={handleOpen}
                startIcon={<PersonAddAltOutlinedIcon />}
                className="lg:text-lg md:text-base font-bold py-4"
                label={fullName}
                fullWidth
              />
              {displayError(error.type, errorMsg, classes)}
            </div>

            {/* Déroulement des séances : description habituelle, personnalisable pour ce cours */}
            <SessionDescriptionCard
              teachr={teachr}
              value={sessionDescriptionOverride}
              onChange={(next) => { setSessionDescriptionOverride(next); setOverrideError(""); }}
              onGenericLoaded={setGenericDescription}
            />
            {displayError(overrideError !== "", overrideError, classes)}

            <p className={`${common.body} ${classes.primaryText} font-semibold mb-4`}>
              De quel type de {getActivePrestationBrandConfig("")} s'agit-il ?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                disabled={travelLoading || descriptionLoading}
                onClick={() => handleNextStep(2, false)}
                onMouseEnter={() => setHoveredCard("ponctuel")}
                onMouseLeave={() => setHoveredCard(null)}
                className="p-5 rounded-md border-2 text-left transition-all duration-200 bg-white cursor-pointer hover:shadow-md disabled:opacity-60"
                style={{ borderColor: hoveredCard === "ponctuel" ? brandConfig.colors.primary : "#E5E7EB" }}
              >
                <div className="text-3xl mb-2">📅</div>
                <div className={`font-bold text-base ${classes.fontFamily} ${classes.text} mb-1`}>
                  {capitalizeFirstLetter(getActivePrestationBrandConfig(" ponctuelle", "", "", " ponctuel"))}
                </div>
                <div className={`text-xs ${classes.fontFamily} text-gray-500`}>
                  {getActivePrestationBrandConfig("", "Un ", "Une ")} à venir, à une date précise
                </div>
              </button>

              <button
                type="button"
                disabled={travelLoading || descriptionLoading}
                onClick={() => handleNextStep(2, true)}
                onMouseEnter={() => setHoveredCard("multiple")}
                onMouseLeave={() => setHoveredCard(null)}
                className="p-5 rounded-md border-2 text-left transition-all duration-200 bg-white cursor-pointer hover:shadow-md disabled:opacity-60"
                style={{ borderColor: hoveredCard === "multiple" ? brandConfig.colors.primary : "#E5E7EB" }}
              >
                <div className="text-3xl mb-2">📚</div>
                <div className={`font-bold text-base ${classes.fontFamily} ${classes.text} mb-1`}>
                  Plusieurs {getActivePrestationBrandConfig("s")}
                </div>
                <div className={`text-xs ${classes.fontFamily} text-gray-500`}>
                  Déclarer plusieurs heures de {getActivePrestationBrandConfig()} étalées sur plusieurs jours
                </div>
              </button>
            </div>
          </>
        }
        isHere={true}
        value={3}
        step={2}
        generalStep={generalStep}
      />
    </>
  );
};

export default FirstStep;
